<script setup>
import { ref, onMounted, watch } from 'vue'
import PersonalDetailsForm from '@/Components/Forms/PersonalDetailsForm.vue'
import SubjectsSelection from '@/Components/Forms/SubjectsSelection.vue'
import CourseLevelsSelection from '@/Components/Forms/CourseLevelsSelection.vue'
import QualificationsForm from '@/Components/Forms/QualificationsForm.vue'
import NavigationButtons from '@/Components/Onboarding/NavigationButtons.vue'

const props = defineProps({
  subjects: Array,
  levels: Array,
  teachingExperiences: Array,
  qualificationOptions: Object,
  application: Object,
  slug: String,
})

const emit = defineEmits(['next', 'submit'])

// Personal details object - matching TutorApplication model
const personalDetails = ref({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  date_of_birth: '',
  address: '',
  city: '',
  postcode: '',
  country: 'United Kingdom',
  timezone: 'Europe/London',
})

// Selection arrays - matching TutorApplication model
const selectedSubjects = ref([])
const selectedLevels = ref([])

// Qualifications object - matching TutorApplication model
// New structure: degrees array + a_levels array (separated)
const qualifications = ref({
  degrees: [],
  a_levels: [],
  years_of_experience: '',
  teaching_experience_id: null,
  additional_qualifications: '',
})

// Component refs
const personalDetailsRef = ref(null)
const subjectsRef = ref(null)
const levelsRef = ref(null)
const qualificationsRef = ref(null)

// Loading and errors
const loading = ref(false)
const errors = ref({})

// Populate form data from application prop
const populateFromApplication = (app) => {
  if (!app) return

  // Split name into first and last name
  const nameParts = app.name ? app.name.split(' ') : ['', '']
  const firstName = nameParts[0] || ''
  const lastName = nameParts.slice(1).join(' ') || ''

  // Populate personal details
  personalDetails.value = {
    first_name: firstName,
    last_name: lastName,
    email: app.email || '',
    phone: app.phone || '',
    date_of_birth: app.date_of_birth || '',
    address: app.address || '',
    city: app.city || '',
    postcode: app.postcode || '',
    country: app.country || 'United Kingdom',
    timezone: app.timezone || 'Europe/London',
  }

  // Populate selections
  selectedSubjects.value = app.subject_ids || []
  selectedLevels.value = app.level_ids || []

  // Populate qualifications - new structure with degrees array
  if (app.qualifications) {
    qualifications.value = {
      degrees: app.qualifications.degrees || [],
      a_levels: app.qualifications.a_levels || [],
      years_of_experience: app.qualifications.years_of_experience || '',
      teaching_experience_id: app.teaching_experience_ids?.[0] || null,
      additional_qualifications: app.additional_qualifications || '',
    }
  }
}

// Watch for application prop changes
watch(() => props.application, (newApp) => {
  if (newApp) {
    populateFromApplication(newApp)
  }
}, { immediate: true })

const updatePersonalDetails = (newDetails) => {
  personalDetails.value = newDetails
}

const validateForm = () => {
  const components = [
    personalDetailsRef,
    subjectsRef,
    levelsRef,
    qualificationsRef
  ]

  let isValid = true

  // Validate all components
  components.forEach(compRef => {
    if (compRef.value && !compRef.value.validate()) {
      isValid = false
    }
  })

  return isValid
}

const saveDraft = async () => {
  // Don't validate for draft - just save whatever data is there
  loading.value = true
  errors.value = {}

  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Accept': 'application/json',
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    }

    // Add slug to header if available
    if (props.slug) {
      headers['X-Tutor-Slug'] = props.slug
    }

    const response = await fetch('/become-a-tutor/personal-information', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        is_draft: true, // Indicate this is a draft save
        // Personal details
        first_name: personalDetails.value.first_name,
        last_name: personalDetails.value.last_name,
        email: personalDetails.value.email,
        phone: personalDetails.value.phone,
        date_of_birth: personalDetails.value.date_of_birth,
        address: personalDetails.value.address,
        city: personalDetails.value.city,
        postcode: personalDetails.value.postcode,
        country: personalDetails.value.country,
        timezone: personalDetails.value.timezone,
        // Selections - matching TutorApplication model
        subject_ids: selectedSubjects.value,
        level_ids: selectedLevels.value,
        // Qualifications - new structure with degrees array
        degrees: qualifications.value.degrees,
        a_levels: qualifications.value.a_levels,
        years_of_experience: qualifications.value.years_of_experience,
        teaching_experience_id: qualifications.value.teaching_experience_id,
        additional_qualifications: qualifications.value.additional_qualifications,
      })
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        // Flatten Laravel's array-based error format to strings
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key])
            ? data.errors[key][0]
            : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save draft. Please try again.' }
      }
      loading.value = false
      return
    }

    // Save slug to localStorage
    if (data.slug) {
      localStorage.setItem('tutor_onboarding_slug', data.slug)
    }

    loading.value = false

    // Show success message
    errors.value = { success: 'Draft saved successfully!' }
    setTimeout(() => {
      errors.value = {}
    }, 3000)

  } catch (err) {
    errors.value = { general: 'An error occurred while saving draft. Please try again.' }
    loading.value = false
  }
}

const submitForm = async () => {
  if (!validateForm()) {
    // Scroll to the first visible error message
    setTimeout(() => {
      const errorMessages = document.querySelectorAll('p.text-red-500, div.text-red-500')
      const firstVisibleError = Array.from(errorMessages).find(el =>
        el.offsetParent !== null && el.textContent.trim().length > 1
      )
      if (firstVisibleError) {
        firstVisibleError.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
    return
  }

  loading.value = true
  errors.value = {}

  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Accept': 'application/json',
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    }

    // Add slug to header if available
    if (props.slug) {
      headers['X-Tutor-Slug'] = props.slug
    }

    const response = await fetch('/become-a-tutor/personal-information', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        // Personal details
        first_name: personalDetails.value.first_name,
        last_name: personalDetails.value.last_name,
        email: personalDetails.value.email,
        phone: personalDetails.value.phone,
        date_of_birth: personalDetails.value.date_of_birth,
        address: personalDetails.value.address,
        city: personalDetails.value.city,
        postcode: personalDetails.value.postcode,
        country: personalDetails.value.country,
        timezone: personalDetails.value.timezone,
        // Selections - matching TutorApplication model
        subject_ids: selectedSubjects.value,
        level_ids: selectedLevels.value,
        // Qualifications - new structure with degrees array
        degrees: qualifications.value.degrees,
        a_levels: qualifications.value.a_levels,
        years_of_experience: qualifications.value.years_of_experience,
        teaching_experience_id: qualifications.value.teaching_experience_id,
        additional_qualifications: qualifications.value.additional_qualifications,
      })
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        // Flatten Laravel's array-based error format to strings
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key])
            ? data.errors[key][0]
            : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      loading.value = false

      // Scroll to the first visible error message
      setTimeout(() => {
        const errorMessages = document.querySelectorAll('p.text-red-500, div.text-red-500')
        const firstVisibleError = Array.from(errorMessages).find(el =>
          el.offsetParent !== null && el.textContent.trim().length > 1
        )
        if (firstVisibleError) {
          firstVisibleError.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      return
    }

    // Save slug and step to localStorage
    if (data.slug) {
      localStorage.setItem('tutor_onboarding_slug', data.slug)
      localStorage.setItem('tutor_onboarding_step', '2')
    }

    loading.value = false

    // Emit next event to parent
    emit('next', data)

  } catch (err) {
    errors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}
</script>

<template>
  <div class="grid lg:grid-cols-4 gap-8">
    <!-- Left Sidebar - Only visible on large screens -->
    <aside id="sidebar-info" class="hidden lg:block lg:col-span-1">
      <div class="sticky top-24">
        <!-- Progress Indicator -->
        <div class="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h3 class="text-lg font-bold text-gray-900 mb-4">Application Progress</h3>
          <div class="space-y-4">
            <!-- Step 1: Basic Information -->
            <div class="flex items-start space-x-3">
              <div class="w-6 h-6 bg-brand-blue rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span class="material-symbols-outlined text-white text-xs">check</span>
              </div>
              <div>
                <p class="font-semibold text-gray-900 text-sm">Basic Information</p>
                <p class="text-xs text-gray-500">Name, email, phone</p>
              </div>
            </div>

            <!-- Step 2: Subjects & Levels -->
            <div class="flex items-start space-x-3">
              <div class="w-6 h-6 bg-brand-blue rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span class="material-symbols-outlined text-white text-xs animate-spin">progress_activity</span>
              </div>
              <div>
                <p class="font-semibold text-gray-900 text-sm">Subjects & Levels</p>
                <p class="text-xs text-gray-500">What you teach</p>
              </div>
            </div>

            <!-- Step 3: Exam Boards -->
            <div class="flex items-start space-x-3">
              <div class="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span class="text-gray-400 text-xs">3</span>
              </div>
              <div>
                <p class="font-semibold text-gray-500 text-sm">Exam Boards</p>
                <p class="text-xs text-gray-400">Your expertise</p>
              </div>
            </div>

            <!-- Step 4: Course Levels -->
            <div class="flex items-start space-x-3">
              <div class="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span class="text-gray-400 text-xs">4</span>
              </div>
              <div>
                <p class="font-semibold text-gray-500 text-sm">Course Levels</p>
                <p class="text-xs text-gray-400">Teaching experience</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Pro Tip Card -->
        <div class="bg-gradient-to-br from-brand-blue to-brand-purple rounded-2xl p-6 text-white">
          <span class="material-symbols-outlined text-3xl mb-3">lightbulb</span>
          <h3 class="text-lg font-bold mb-2">Pro Tip</h3>
          <p class="text-sm text-blue-100">Tutors who complete their profile fully get 3x more student requests!</p>
        </div>
      </div>
    </aside>

    <!-- Main Content -->
    <div class="lg:col-span-3">
      <form @submit.prevent="submitForm" novalidate class="space-y-8">
        <!-- Success Message -->
        <div v-if="errors.success" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
          <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
            <span class="material-symbols-outlined text-white text-xl">check_circle</span>
          </div>
          <div>
            <p class="text-green-900 font-semibold">Success</p>
            <p class="text-green-700 text-sm">{{ errors.success }}</p>
          </div>
        </div>

        <!-- General Error Message -->
        <div v-if="errors.general" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
          <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
            <span class="material-symbols-outlined text-white text-xl">error</span>
          </div>
          <div>
            <p class="text-red-900 font-semibold">Error</p>
            <p class="text-red-700 text-sm">{{ errors.general }}</p>
          </div>
        </div>

        <!-- Personal Details Component -->
        <PersonalDetailsForm
          ref="personalDetailsRef"
          :model-value="personalDetails"
          @update:model-value="updatePersonalDetails"
          :errors="errors"
        />

        <!-- Subjects Selection Component -->
        <SubjectsSelection
      ref="subjectsRef"
      :subjects="subjects"
      :model-value="selectedSubjects"
      @update:model-value="(val) => selectedSubjects = val"
      :errors="errors"
    />

    <!-- Course Levels Selection Component -->
    <CourseLevelsSelection
      ref="levelsRef"
      :levels="levels"
      :model-value="selectedLevels"
      @update:model-value="(val) => selectedLevels = val"
      :errors="errors"
    />

    <!-- Qualifications Component -->
    <QualificationsForm
      ref="qualificationsRef"
      :model-value="qualifications"
      :teaching-experiences="teachingExperiences"
      :subjects="subjects"
      :qualification-options="qualificationOptions"
      @update:model-value="(val) => qualifications = val"
      :errors="errors"
    />

      </form>
    </div>
  </div>

  <!-- Navigation Buttons -->
  <NavigationButtons
    :loading="loading"
    :show-previous="false"
    primary-button-text="Continue to Services Offered"
    primary-button-loading-text="Saving..."
    @save-draft="saveDraft"
    @submit="submitForm"
  />
</template>
